import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';

import { LoadingIndicator } from 'features/store/pages/pickup-store-selector/components';
import { FlatList, StyleSheet, ViewStyle } from 'react-native';

import { LayoutContainer } from 'components/layout/styled';
import useFilterModal from 'components/nutrition/use-filter-modal';
import { Box, BoxProps, Typography } from 'components/ui';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { INutritionExplorerWidget } from 'remote/queries/fragments/nutrition-explorer-widget';
import { useErrorContext } from 'state/errors';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { layout, primitive, shadows, spacing } from 'styles/constants';

import { Accordion } from './accordion';
import { MoreOptions } from './more-options';
import { Tags } from './tags';
import { IFilter, NutritionExplorerMenuObjectProductsTuple } from './types';
import { filterData, getFilterGroup, getParsedMenuData } from './utils';

const MadLibSelect = lazy(() => import('./mad-lib-select'));

const BlackBackground = ({ style, ...props }: BoxProps) => {
  const isMobile = useIsMobileBreakpoint();

  return (
    <Box
      {...props}
      style={[styles.blackBackground, !isMobile && styles.blackBackgroundDesktop, style]}
    />
  );
};

const Content = ({ style, ...props }: BoxProps) => {
  const isMobile = useIsMobileBreakpoint();

  return <Box {...props} style={[styles.content, !isMobile && styles.contentDesktop, style]} />;
};

const InnerContainer = ({ style, ...props }: BoxProps) => {
  const isMobile = useIsMobileBreakpoint();

  return (
    <Box
      {...props}
      style={[styles.innerContainer, !isMobile && styles.innerContainerDesktop, style]}
    />
  );
};

const FiltersContainer = ({ style, ...props }: BoxProps) => {
  const isMobile = useIsMobileBreakpoint();

  return (
    <Box
      {...props}
      style={[
        styles.innerContainer,
        !isMobile && styles.innerContainerDesktop,
        styles.filtersContainer,
        !isMobile && styles.filtersContainerDesktop,
        style,
      ]}
    />
  );
};

const MadLibSelectPlaceholder = ({ style, ...props }: BoxProps) => {
  const isMobile = useIsMobileBreakpoint();

  return (
    <Box
      {...props}
      style={[
        styles.madLibSelectPlaceholder,
        !isMobile && styles.madLibSelectPlaceholderDesktop,
        style,
      ]}
    />
  );
};

const NutritionExplorerWidget: React.FC<React.PropsWithChildren<INutritionExplorerWidget>> = ({
  menu,
  categoryWhitelist,
  viewMenuButtonText,
  moreOptionsButtonText,
  madLibFilterGroup: sanityMadLibFilterGroup,
  modalFilterGroups: sanityModalFilterGroups,
}) => {
  const { setSanityDataRef } = useErrorContext();
  const enableNutritionExplorerAllergens = useFlag(
    LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER_ALLERGENS
  );

  const isMobile = useIsMobileBreakpoint();

  useEffect(() => {
    setSanityDataRef({
      data: {
        menu,
        categoryWhitelist,
        viewMenuButtonText,
        moreOptionsButtonText,
        sanityMadLibFilterGroup,
        sanityModalFilterGroups,
      },
    });
  }, [
    menu,
    categoryWhitelist,
    viewMenuButtonText,
    moreOptionsButtonText,
    sanityMadLibFilterGroup,
    sanityModalFilterGroups,
    setSanityDataRef,
  ]);

  const {
    NutritionFilterModal,
    openModal: openFilterModal,
    closeModal: closeFilterModal,
    isModalOpen: isFilterModalOpen,
  } = useFilterModal();

  const [activeMadLibFilter, setActiveMadLibFilter] = useState<IFilter | null>(null);
  const [activeModalFilters, setActiveModalFilters] = useState<IFilter[]>([]);

  const parsedData: NutritionExplorerMenuObjectProductsTuple[] = useMemo(
    () =>
      getParsedMenuData({
        menu,
        categoryWhitelist,
        enableNutritionExplorerAllergens,
      }),
    [menu, categoryWhitelist, enableNutritionExplorerAllergens]
  );

  const madLibFilterGroup = useMemo(
    () => (sanityMadLibFilterGroup ? getFilterGroup(sanityMadLibFilterGroup) : null),
    [sanityMadLibFilterGroup]
  );

  const modalFilterGroups = useMemo(() => (sanityModalFilterGroups ?? []).map(getFilterGroup), [
    sanityModalFilterGroups,
  ]);

  const filteredData: NutritionExplorerMenuObjectProductsTuple[] = useMemo(() => {
    const activeFilters: IFilter[] = activeModalFilters.concat(
      activeMadLibFilter ? [activeMadLibFilter] : []
    );

    return filterData(parsedData, activeFilters);
  }, [parsedData, activeModalFilters, activeMadLibFilter]);

  const productsCount = useMemo(
    () => filteredData.reduce((totalCount, [, products]) => totalCount + products.length, 0),
    [filteredData]
  );

  const onMadLibFilterChange = useCallback((newFilter?: IFilter | null) => {
    setActiveMadLibFilter(newFilter ?? null);
  }, []);

  const onOpenFilterModal = useCallback(() => {
    openFilterModal();
  }, [openFilterModal]);

  return (
    <LayoutContainer testID="nutrition-explorer" isFullContainer={isMobile}>
      <BlackBackground />
      <Content>
        <InnerContainer style={styles.filtersLayer}>
          <FiltersContainer>
            <Suspense
              fallback={
                <MadLibSelectPlaceholder>
                  <LoadingIndicator />
                </MadLibSelectPlaceholder>
              }
            >
              <MadLibSelect
                prompt={madLibFilterGroup?.description?.locale}
                selectedFilter={activeMadLibFilter}
                filters={madLibFilterGroup?.filters ?? []}
                onFilterChange={onMadLibFilterChange}
              />
              <Tags activeModalFilters={activeModalFilters} openFilterModal={onOpenFilterModal} />
              <MoreOptions
                openFilterModal={onOpenFilterModal}
                buttonText={moreOptionsButtonText?.locale || t('more')}
              />
            </Suspense>
          </FiltersContainer>
        </InnerContainer>
        <InnerContainer>
          <Typography>{t('nutritionCommitment')}</Typography>
        </InnerContainer>
        <InnerContainer>
          <FlatList
            data={filteredData}
            keyExtractor={item => item[0]._id}
            renderItem={({ item: [parentMenuObject, products], index }) => (
              <Accordion
                hasBorderTop={index > 0}
                parentMenuObject={parentMenuObject}
                products={products}
                startsOpen={index === 0}
                key={parentMenuObject._id}
              />
            )}
          />
        </InnerContainer>
      </Content>
      <NutritionFilterModal
        resultsLeft={productsCount}
        isModalOpen={isFilterModalOpen}
        filterGroups={modalFilterGroups}
        activeFilters={activeModalFilters}
        onDismiss={closeFilterModal}
        applyFilters={setActiveModalFilters}
      />
    </LayoutContainer>
  );
};

export default NutritionExplorerWidget;

type WebShadowViewStyle = ViewStyle & {
  boxShadow?: string;
};

const styles = StyleSheet.create({
  blackBackground: {
    backgroundColor: primitive.$bbqBrown,
    height: spacing.$32,
    left: 0,
    position: 'absolute',
    top: 0,
    width: layout.full,
  },
  blackBackgroundDesktop: {
    height: spacing.$48,
  },
  content: {
    alignItems: 'center',
    paddingBottom: spacing.$13_5,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$12,
    width: layout.full,
  },
  contentDesktop: {
    paddingBottom: spacing.$20,
    paddingHorizontal: spacing.$0,
  },
  filtersContainer: {
    ...shadows.$1,
    backgroundColor: primitive.$white,
    borderRadius: 8,
    paddingHorizontal: spacing.$2,
    paddingVertical: spacing.$2,
    width: layout.full,
  } as WebShadowViewStyle,
  filtersContainerDesktop: {
    paddingHorizontal: spacing.$13_5,
    paddingVertical: spacing.$8,
  },
  filtersLayer: {
    zIndex: 2,
  },
  innerContainer: {
    paddingBottom: spacing.$4,
    width: layout.full,
  },
  innerContainerDesktop: {
    paddingBottom: spacing.$5,
  },
  madLibSelectPlaceholder: {
    alignItems: 'center',
    flexBasis: 'auto',
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
    height: 58,
    justifyContent: 'center',
  },
  madLibSelectPlaceholderDesktop: {
    height: 82,
  },
});
